import type { ReactNode } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./card";
import { cn } from "../lib/utils";

interface SettingsSectionProps {
  /** Section heading. Pass an already-translated string. */
  title: string;
  /** Optional muted supporting copy under the heading. */
  description?: string;
  /** Section body — typically a stack of labelled fields. */
  children: ReactNode;
  /**
   * Optional footer slot, used for the per-section save / primary affordance.
   * Rendered in a subtle bordered footer band, right-aligned on wide screens.
   */
  footer?: ReactNode;
  className?: string;
}

/**
 * Card-based grouping for a block of related settings. Gives every settings
 * screen a consistent structure: a small section title + description, a padded
 * body for fields, and an optional bottom band for the save affordance.
 *
 * Composes the shadcn Card primitive — does not introduce new tokens. The body
 * stacks children with `gap-6` so callers can drop fields in without managing
 * vertical rhythm themselves.
 *
 * Presentational only — pass already-translated strings.
 */
export function SettingsSection({
  title,
  description,
  children,
  footer,
  className,
}: SettingsSectionProps) {
  return (
    <Card className={cn("gap-0 py-0", className)}>
      <CardHeader className="border-b px-6 py-5">
        <CardTitle className="text-base">{title}</CardTitle>
        {description && <CardDescription className="text-pretty">{description}</CardDescription>}
      </CardHeader>
      <CardContent className="flex flex-col gap-6 px-6 py-6">{children}</CardContent>
      {footer && (
        <CardFooter className="bg-muted/30 flex justify-end border-t px-6 py-4">
          {footer}
        </CardFooter>
      )}
    </Card>
  );
}

interface SettingsFieldProps {
  /** Field label text. Pass an already-translated string. */
  label: string;
  /** Optional muted helper text shown under the value/control. */
  hint?: string;
  /** Optional id to wire `<label htmlFor>` to a control inside `children`. */
  htmlFor?: string;
  /** The field control or read-only value. */
  children: ReactNode;
  className?: string;
}

/**
 * A single labelled row inside a SettingsSection. Renders a semantic `<label>`
 * (associated via `htmlFor` when an interactive control is provided), the field
 * control, and optional helper text. Keeps label/control/hint spacing uniform
 * across every settings field.
 */
export function SettingsField({ label, hint, htmlFor, children, className }: SettingsFieldProps) {
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <label htmlFor={htmlFor} className="text-foreground text-sm leading-none font-medium">
        {label}
      </label>
      {children}
      {hint && <p className="text-muted-foreground text-xs text-pretty">{hint}</p>}
    </div>
  );
}

export type { SettingsSectionProps, SettingsFieldProps };
